import type {
  ITaskLogEntry,
  IChecklistItem,
  IChecklistItemCompletion,
  ISchedulerComment,
  ISchedulerData,
  ISchedulerTask,
  ITaskStockClaim,
  TaskId,
} from '@interfaces/IScheduler';

// ---------------------------------------------------------------------------
// taskViewCodec — the ONE frontend module that names the execution fields.
//
// A real-world unit of work has two facets (see the task/activity domain-split
// design, its phase-2 companion, and the 2026-09-17 task-commit design): the
// PLAN facet (name, dates, WBS, dependencies, plan STATUS, commit KIND, area
// ASSIGNMENT, checklist DEFINITIONS) and the EXECUTION facet — the overlay on a
// committed Trusstor activity (progressPercent, server-generated log, comments,
// checklist COMPLETION, stock claims). The backend stores them in two
// collections and composes them at read time; the frontend mirrors that split:
//   • `scheduler.slice`'s `bySite` holds PLAN-only tasks.
//   • `scheduler.slice`'s `activityBySite[siteId][taskId]` holds the execution
//     facet of a COMMITTED task, written by the immediate-write surfaces
//     (progress / comments / checks / stock claims). Its PRESENCE is what
//     `committed` means — there is no flag to drift from it.
//   • Selectors compose the two into a `TaskView` (a full `ISchedulerTask`) so
//     components keep reading `task.progressPercent` etc. unchanged.
//
// Write paths never consult a field list — they are facet-scoped by shape. The
// only place the execution field NAMES appear is here, so adding an
// execution field is a one-file change (this codec + the execution reducers).
// This module replaces the deleted `IMMEDIATE_WRITE_TASK_FIELDS` /
// `mergeImmediateWriteFields` machinery.
// ---------------------------------------------------------------------------

/**
 * The execution facet of a task — the overlay on its committed Trusstor
 * activity. ABSENCE MEANS UNCOMMITTED (invariant 2): the task is plan only,
 * which is the normal state of most rows, and every execution write against it
 * is refused by the server. The store materialises the collections so reducers
 * can push/assign without a nullish guard.
 *
 * Phase 2 moved two more facets here off the plan doc: `comments` (a comment is
 * conversation about doing the work) and `checklistCompletion` (a tick + its
 * photo/blackboard evidence is a record of work done), keyed by checklist-item
 * id. The composed selectors re-nest completion into the checklist items and set
 * `comments`, so the drawer's checklist/comments components keep their props.
 *
 * "Stock from CAD" (ruling 9, 2026-09-09) moved `stockClaims` here too: a claim
 * records what the work consumes, so it lives on the activities document and
 * persists through its own per-task PUT — never the whole-schedule Save.
 */
export interface IActivity {
  /** `null` for an `administrative` activity — it has no Trusstor side. */
  trusstorGroupId: string | null;
  /** Trusstor manager username the activity is assigned to. */
  assignee: string | null;
  dangerousActivity: boolean;
  progressPercent: number;
  log: ITaskLogEntry[];
  comments: ISchedulerComment[];
  checklistCompletion: Record<string, IChecklistItemCompletion>;
  stockClaims: ITaskStockClaim[];
}

/**
 * A task with its execution facet removed — the shape stored in `bySite`.
 * `comments` is execution-owned (phase 2), `stockClaims` too (ruling 9).
 * `checklist` stays (DEFINITIONS are plan-owned), but each item's execution
 * `completion` is stripped by `splitTaskView` — a plan checklist item never
 * carries it.
 */
export type ISchedulerPlanTask = Omit<
  ISchedulerTask,
  'progressPercent' | 'log' | 'comments' | 'stockClaims' | 'committed' | 'dangerousActivity' | 'assignee'
>;

/** A schedule whose tasks are plan-only (the shape stored per site in `bySite`). */
export type ISchedulerPlanData = Omit<ISchedulerData, 'tasks'> & {
  tasks: ISchedulerPlanTask[];
};

/** Fresh default execution facet — the state a task has with no stored record. */
export function DEFAULT_EXECUTION(): IActivity {
  return {
    trusstorGroupId: null,
    assignee: null,
    dangerousActivity: false,
    progressPercent: 0,
    log: [],
    comments: [],
    checklistCompletion: {},
    stockClaims: [],
  };
}

/**
 * Split a composed `TaskView` (or an import/save-response task) into its two
 * facets. An UNCOMMITTED task yields no execution facet at all. Missing
 * execution fields fall back to the defaults, so a server task that omits empty
 * `log`/`comments` still yields well-formed collections. Each checklist item's execution `completion` is collapsed into
 * `checklistCompletion[itemId]` and stripped from the plan checklist item — the
 * plan keeps DEFINITIONS only (mirrors the backend `extractExecution` /
 * `toPlanTask`).
 */
export function splitTaskView(task: ISchedulerTask): {
  plan: ISchedulerPlanTask;
  /** `undefined` when the task is UNCOMMITTED — there is no overlay to hold. */
  execution: IActivity | undefined;
} {
  const {
    progressPercent, log, comments, stockClaims, checklist,
    committed, dangerousActivity, assignee,
    ...rest
  } = task;
  const checklistCompletion: Record<string, IChecklistItemCompletion> = {};
  let planChecklist: IChecklistItem[] | undefined;
  if (checklist) {
    planChecklist = checklist.map(({ completion, ...definition }) => {
      if (completion) checklistCompletion[definition.id] = completion;
      return definition;
    });
  }
  const plan: ISchedulerPlanTask = planChecklist ? { ...rest, checklist: planChecklist } : { ...rest };
  return {
    plan,
    execution: committed
      ? {
          trusstorGroupId: null, // the wire does not carry it; the row only needs `committed`
          assignee: assignee ?? null,
          dangerousActivity: dangerousActivity ?? false,
          progressPercent,
          log: log ?? [],
          comments: comments ?? [],
          checklistCompletion,
          stockClaims: stockClaims ?? [],
        }
      : undefined,
  };
}

/**
 * Compose a plan task with its execution facet into a full `TaskView`. Empty
 * `log`/`comments`/`stockClaims` are emitted as ABSENT (not `[]`) to match
 * the wire / server-composed shape, so round-trips and diffs stay stable. The
 * per-item completion is re-nested into the composed checklist items (as
 * `completion`), so the drawer's checklist/comments components see the
 * pre-phase-2 shape (mirrors the backend `composeTaskView`).
 *
 * Pure: always mints a fresh view. `composeScheduleData` uses the memoized
 * variant below for reference stability; other callers keep using this directly
 * via their own narrow `useMemo`.
 */
export function composeTaskView(
  plan: ISchedulerPlanTask,
  execution: IActivity | undefined,
): ISchedulerTask {
  const e = execution ?? DEFAULT_EXECUTION();
  const view: ISchedulerTask = {
    ...plan,
    committed: execution !== undefined,
    // Done IS 100 %, committed or not: the plan's done is the final word on
    // the work, so a promise marked done early (or finished without field
    // tracking) never reads "Done · 0%". Otherwise the activity's own % (0 for
    // an uncommitted task). Mirrors the backend `composeTaskView`. Composed,
    // not stored: a re-open shows the activity's own % again.
    progressPercent: plan.status === 'done' ? 100 : e.progressPercent,
  };
  if (execution) {
    view.dangerousActivity = e.dangerousActivity;
    view.assignee = e.assignee;
  }
  if (e.log.length > 0) view.log = e.log;
  if (e.comments.length > 0) view.comments = e.comments;
  if (e.stockClaims.length > 0) view.stockClaims = e.stockClaims;
  if (plan.checklist && plan.checklist.length > 0) {
    view.checklist = plan.checklist.map((item) => {
      const completion = e.checklistCompletion[item.id];
      return completion ? { ...item, completion } : { ...item };
    });
  }
  return view;
}

/**
 * Split a whole fetched schedule into a plan-only doc + the per-task execution
 * map. Used by `bootstrap` to seed both slices from one server payload.
 */
export function splitScheduleData(data: ISchedulerData): {
  plan: ISchedulerPlanData;
  executionByTaskId: Record<TaskId, IActivity>;
} {
  const tasks: ISchedulerPlanTask[] = [];
  const executionByTaskId: Record<TaskId, IActivity> = {};
  for (const task of data.tasks) {
    const { plan, execution } = splitTaskView(task);
    tasks.push(plan);
    // Only COMMITTED tasks get an entry: presence in this map IS `committed`.
    if (execution) executionByTaskId[task.id] = execution;
  }
  return { plan: { ...data, tasks }, executionByTaskId };
}

// ---------------------------------------------------------------------------
// Identity-preserving composition.
//
// `composeScheduleData` runs on every store read — including every slider
// pointer-move frame, where the execution reducers mint a fresh
// `activityBySite[siteId]` ref per write. Minting a fresh view object per task
// on every call would re-render every `memo()`'d bar/label across the whole
// schedule per frame.
//
// The WeakMap below is keyed on plan-task object identity, exploiting Immer's
// structural sharing: untouched plan tasks AND untouched execution records keep
// their refs across writes, so their composed views are reused verbatim. A view
// only recomputes when its plan task OR its execution record actually changes.
//
// WeakMap (not a hook-level Map): plan-task objects are globally unique per
// site, so there is no cross-site contamination; GC evicts dropped tasks with
// no manual invalidation, no unbounded growth, no double-cache on dual mounts.
//
// IMMUTABILITY CAVEAT: the views returned here are SHARED references, cached and
// handed to multiple callers/frames. Treat them as immutable — never mutate a
// composed view in place. Mutation would corrupt the cache and every consumer
// holding the same ref.
// ---------------------------------------------------------------------------

interface IComposedTaskCacheEntry {
  execution: IActivity | undefined;
  view: ISchedulerTask;
}

const composedTaskCache = new WeakMap<ISchedulerPlanTask, IComposedTaskCacheEntry>();

/**
 * Reference-stable wrapper around `composeTaskView`: returns the SAME view
 * object as long as neither the plan task nor its execution record changed
 * identity. See the caveat above — the returned view is shared and immutable.
 */
function composeTaskViewMemoized(
  plan: ISchedulerPlanTask,
  execution: IActivity | undefined,
): ISchedulerTask {
  const cached = composedTaskCache.get(plan);
  if (cached && cached.execution === execution) return cached.view;
  const view = composeTaskView(plan, execution);
  composedTaskCache.set(plan, { execution, view });
  return view;
}

/**
 * Compose a plan-only doc with the per-task execution map into a full schedule
 * (`TaskView[]`) — the read-path join used by the store selectors. A plan task
 * with no execution entry composes to defaults (invariant: absence == default).
 *
 * Uses the memoized composer, so untouched tasks keep their view refs across
 * writes. Callers must treat the composed views as immutable (see caveat above).
 */
export function composeScheduleData(
  plan: ISchedulerPlanData,
  executionByTaskId: Record<TaskId, IActivity> | undefined,
): ISchedulerData {
  return {
    ...plan,
    tasks: plan.tasks.map((task) => composeTaskViewMemoized(task, executionByTaskId?.[task.id])),
  };
}

/**
 * `data` with one task's execution facet replaced by `execution` (undefined:
 * uncommitted) — its plan half, and every other task, untouched. Lets a cached
 * schedule follow an immediate write without being re-read from the server,
 * which would also pull in other editors' plan changes (QA F-90).
 */
export function withTaskExecution(data: ISchedulerData, taskId: TaskId, execution: IActivity | undefined): ISchedulerData {
  return {
    ...data,
    tasks: data.tasks.map((task) => (task.id === taskId ? composeTaskView(splitTaskView(task).plan, execution) : task)),
  };
}
